import store from 'config/store'
import { triggerChatVisibleAPIEvent } from 'modules/api/events'
import { showLastCampaignInSession, storeAnnouncementCampaignCompleted } from 'modules/campaigns'
import { showLatestSessionConversation } from 'modules/conversations'
import { removeSendIdFromStore } from 'modules/impressions/utils'
import { resetControllerSize } from 'modules/view'
import { pathOr } from 'ramda'
import { filter, ignoreElements, map, tap } from 'rxjs/operators'
import { CampaignDisplayType } from 'types/campaigns'
import { ofType } from 'utils/redux'

import { toggleChatClosed, toggleChatOpen, toggleChatTakeover } from './actions'

const activeCampaign = (state) => state.campaigns.activeCampaign

const hasActiveConversation = (state) => !!state.conversations.activeConversation

/**
 * Determines what fallback content, if any, to show a site visitor in the following ways:
 * 1) If we have an active conversation we don't need fallback content so exit out
 * 2) Attempt to show the most recent conversation this session
 * 3) Attempt to show the most recent campaign sent to the user this session
 * 4) If all else fails, the default greeting will automatically kick in
 */
const showFallbackChatContentIfNeeded = async () => {
  if (hasActiveConversation(store.getState())) {
    return
  }

  const shownRecentConversation = await showLatestSessionConversation()
  if (shownRecentConversation) {
    return
  }

  await showLastCampaignInSession()
}

/**
 * Handles side effects from toggling the chat either via API or by clicking on the widget icon or preview message directly
 * Results in an action being dispatched to tell the reducer to open/close the chat
 * @param action$ - Redux action reference
 */
export const onToggleChatEpic = (action$) => action$.pipe(
  ofType("TOGGLE_CHAT"),
  filter(() => store.name === 'controller'),
  tap(async ({ payload }) => {
    triggerChatVisibleAPIEvent(payload.chatOpen)

    if (payload.isChatTakeover) {
      store.dispatch(toggleChatTakeover(payload.isChatTakeover))
    }

    if (payload.chatOpen) {
      resetControllerSize()

      if (!payload.displayGreeting) {
        await showFallbackChatContentIfNeeded()
      }

      store.dispatch(toggleChatOpen())
    } else {
      store.dispatch(toggleChatClosed())
    }
  }),
  ignoreElements()
)

/**
 * mark welcome message announcements "completed" when you "close" the chat.
 * closing can be triggered by the widget API, by the widget icon, or by the X in the mobile view.
 */
export const onCloseChatCompleteCampaignEpic = (action$) => action$.pipe(
  ofType("TOGGLE_CHAT"),
  filter(() => store.name === 'controller'),
  filter(({ payload }) => !payload.chatOpen), // chatOpen=false when going from open -> closed
  map(() => activeCampaign(store.getState())),
  filter((campaign) => !!campaign),
  filter((campaign) => pathOr(null, ['attributes', 'campaignDisplayType'], campaign) === CampaignDisplayType.WELCOME_MESSAGE),
  tap((campaign) => {
    const campaignId = pathOr(null, ['id'], campaign)
    const playbookId = pathOr(null, ['attributes', 'playbookId'], campaign)

    storeAnnouncementCampaignCompleted(campaignId, playbookId)
    removeSendIdFromStore(playbookId)
  }),
  ignoreElements()
)
